import React, { useState } from "react";

// Lib imports
import PropTypes from 'prop-types';
import Toast from "@moltech/molui/toast";
import { Editor } from "primereact/editor";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { Condition, Field } from "@moltech/molui/molform";

// Utilities imports
import { SwalService } from "../../../utils/common/SwalService";
import { encryptUrlData } from "../../../utils/encryption/CryptoService";

// Component imports
import DynamicTabs from "../../../components/dynamicTabs/DynamicTabs";

// Enum imports
import JobRequisitionStatus from "../../../enums/JobRequisitionStatus";

const MolEditor = ({ value, onChange, ...props }) => (
  <Editor
    value={value}
    onTextChange={(e) => onChange(e.htmlValue)}
    style={{ height: "200px" }}
    {...props}
  />
);

MolEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
};



const urgencyLevelOptions = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];

const ManpowerAcquisitionForm = ({
  departmentOption,
  userOptions,
  skillOptions,
  employmentTypeOptions,
  canEditField,
  validationRule,
  statusOptions,
  jobOpening,
  isAdmin,
  isHR,
  isInterviewer,
  isInterviewerDraft,
  jobRequisitionId,
  defaultValue,
  activeTemplates,
  fetchTemplateUse,
  setDefaultValue,
  setFormKey,
  formRef,
  isEditMode
}) => {
  const currentStatus = Number(defaultValue?.status);
  const isRejected = currentStatus === JobRequisitionStatus.REJECTED;
  const isApproved = currentStatus === JobRequisitionStatus.APPROVED;
  const isDraft = currentStatus === JobRequisitionStatus.DRAFT;
  const currentLevel = Number(defaultValue?.approvalLevel);

  const buildJobDescriptionHtml = (template) => {
    const sections = [
      ["Job Summary", template?.jobSummary],
      ["Responsibilities", template?.responsibilities],
      ["Benefits", template?.benefits],
      ["About Company", template?.aboutCompany],
    ];

    return sections
      .filter(([, content]) => content)
      .map(([title, content]) => `<h3>${title}</h3><p>${content}</p>`)
      .join("<br/>");
  };

  const getEncryptedTemplateId = (templateId) => {
    return process.env.REACT_APP_IS_ENCRYPTION === "true"
      ? encryptUrlData(templateId)
      : templateId;
  };

  const applyTemplateToForm = (template) => {
    const currentData = formRef.current?.getData() || {};

    setDefaultValue({
      ...currentData,
      jobTitle: template?.jobTitle || "",
      designation: template?.jobTitle || "",
      department: template?.departmentId || null,
      employmentType: template?.employmentTypeIds?.[0] || template?.employmentTypeId || null,
      requiredQualifications: template?.qualification || "",
      requiredExperience: template?.experienceFrom ? Math.round(template.experienceFrom) : 0,
      skills: template?.skillMappings?.map((s) => s.skillId) || [],
      jobDescription: buildJobDescriptionHtml(template),
    });

    setFormKey((prev) => prev + 1);
    Toast.success("Form auto-filled from template");
  };

  const loadTemplateForUse = async (templateId) => {
    const encryptedId = getEncryptedTemplateId(templateId);

    const res = await fetchTemplateUse({
      url: `/job-templates/GetJobTemplateForUse?id=${encryptedId}&isFormatRequired=false`,
    });

    if (res?.data?.success) {
      applyTemplateToForm(res.data.data);
    }
  };

  const onTemplateChange = async (templateId) => {
    if (!templateId) return;

    const confirmed = await SwalService.confirm({
      text: "Selecting a template will overwrite current form values. Do you want to proceed?",
      confirmButtonText: "Yes, use template",
    });

    if (!confirmed) return;

    await loadTemplateForUse(templateId);
  };

  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTemplates = activeTemplates?.filter(template =>
    template.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.jobCode.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];
  const today = new Date();

  return (
    <div className="manpower-acquisition-form">
      <DynamicTabs
        tabs={[
          {
            header: "Requisition Overview",
            icon: "pi pi-info-circle",
            content: (
              <div className="tab-pane-content">
                {/* Template Selection */}
                {!isEditMode && (
                  <div className="mr-template-selection-card">
                    <div className="card-info">
                      <div className="template-icon-badge">
                        <i className="pi pi-sparkles template-spark-icon" />
                      </div>
                      <div className="info-text">
                        <h4>Import from Job Template</h4>
                        <p>Select a pre-configured template to automatically fill job details and descriptions.</p>
                      </div>
                    </div>
                    <div className="field-wrapper">
                      {defaultValue?.jobTemplateId && (
                        <div className="selected-template-badge">
                          <span className="badge-text">
                            <i className="pi pi-check-circle mr-1" />
                            Template: {activeTemplates?.find(t => t.jobTemplateId === defaultValue?.jobTemplateId)?.templateName || "Selected"}
                          </span>
                          <Button
                            type="button"
                            icon="pi pi-times"
                            className="p-button-rounded p-button-text p-button-danger clear-badge-btn"
                            tooltip="Clear selection"
                            onClick={() => {
                              setDefaultValue({
                                ...defaultValue,
                                jobTemplateId: null
                              });
                            }}
                          />
                        </div>
                      )}
                      <Button
                        type="button"
                        label="Autofill from Job Template"
                        icon="pi pi-bolt"
                        className="btn primary-btn btn-sm template-action-btn"
                        onClick={() => setShowTemplateDialog(true)}
                      />
                      <Field name="jobTemplateId">
                        {({ input }) => <input type="hidden" {...input} />}
                      </Field>
                    </div>
                  </div>
                )}

                {/* 1. Department Details */}
                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-building mr-2"></i>
                    <h3>1. Department Details</h3>
                  </div>
                  <div className="form-grid">
                    <Field
                      component={Dropdown}
                      options={departmentOption}
                      filter
                      showClear
                      name="department"
                      label="Department Name"
                      placeholder="Select Department"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={Dropdown}
                      options={userOptions}
                      filter
                      showClear
                      name="requestingManager"
                      label="Requesting Manager"
                      placeholder="Select Manager"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={Calendar}
                      name="dateOfRequest"
                      label="Date of Request"
                      placeholder="Select Date"
                      showIcon
                      dateFormat="dd/mm/yy"
                      validation={validationRule}
                      disabled={!canEditField}
                      maxDate={today}
                      required
                    />
                  </div>
                </div>

                {/* 2. Position Details */}
                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-briefcase mr-2"></i>
                    <h3>2. Position Details</h3>
                  </div>
                  <div className="form-grid">
                    <Field
                      component={InputText}
                      name="designation"
                      label="Designation"
                      placeholder="Enter designation"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={InputNumber}
                      name="noOfPositions"
                      label="Number of Positions"
                      placeholder="0"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                      min={1}
                      max={99}
                    />
                    <Field
                      component={InputText}
                      name="jobTitle"
                      label="Job Title"
                      placeholder="Enter job title"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={Dropdown}
                      options={employmentTypeOptions}
                      name="employmentType"
                      label="Employment Type"
                      placeholder="Select Type"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={Dropdown}
                      options={userOptions}
                      filter
                      showClear
                      name="reportingTo"
                      label="Reporting To"
                      placeholder="Select Reporting Manager"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                  </div>
                </div>
              </div>
            ),
          },
          {
            header: "Job Specifications",
            icon: "pi pi-file-edit",
            content: (
              <div className="tab-pane-content">
                {/* 3. Justification for Hiring */}
                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-info-circle mr-2"></i>
                    <h3>3. Justification for Hiring</h3>
                  </div>
                  <div className="form-grid single-col">
                    <Field
                      component={MolEditor}
                      name="reasonForRequest"
                      label="Reason for Request"
                      disabled={!canEditField}
                    />
                  </div>
                </div>

                {/* 4. Job Description */}
                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-file mr-2"></i>
                    <h3>4. Job Description</h3>
                  </div>
                  <div className="form-grid single-col mb-4">
                    <Field
                      component={MolEditor}
                      name="jobDescription"
                      label="Job Description"
                      disabled={!canEditField}
                    />
                  </div>
                </div>
              </div>
            ),
          },
          {
            header: "Requirements & Budget",
            icon: "pi pi-tags",
            content: (
              <div className="tab-pane-content">
                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-user-plus mr-2"></i>
                    <h3>Candidate Requirements</h3>
                  </div>
                  <div className="form-grid">
                    <Field
                      component={InputText}
                      name="requiredQualifications"
                      label="Required Qualifications"
                      placeholder="Enter qualifications..."
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={InputNumber}
                      name="requiredExperience"
                      label="Required Experience (Years)"
                      placeholder="0"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                      min={0}
                    />
                    <Field
                      component={MultiSelect}
                      options={skillOptions}
                      name="skills"
                      label="Skills"
                      placeholder="Select Skills"
                      filter
                      showClear
                      disabled={!canEditField}
                      display="chip"
                    />
                  </div>
                </div>

                <div className="form-section-container">
                  <div className="section-header">
                    <i className="pi pi-money-bill mr-2"></i>
                    <h3>Budget & Timeline</h3>
                  </div>
                  <div className="form-grid">
                    <Field
                      component={InputNumber}
                      name="expectedSalaryLow"
                      label="Min Salary Range"
                      placeholder="Min Salary"
                      mode="currency"
                      currency="INR"
                      locale="en-IN"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <Field
                      component={InputNumber}
                      name="expectedSalaryHigh"
                      label="Max Salary Range"
                      placeholder="Max Salary"
                      mode="currency"
                      currency="INR"
                      locale="en-IN"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                    <div className="mol-field">
                      <label className="font-bold block mb-2" htmlFor="budgetYes">Budget Availability</label>
                      <div className="radio-group">
                        <div className="radio-item">
                          <Field
                            component={RadioButton}
                            name="budgetAvailability"
                            label="Yes"
                            id="budgetYes"
                            value="Yes"
                            type="radio"
                            disabled={!canEditField}
                          />
                        </div>
                        <div className="radio-item">
                          <Field
                            component={RadioButton}
                            name="budgetAvailability"
                            label="No"
                            id="budgetNo"
                            value="No"
                            type="radio"
                            disabled={!canEditField}
                          />
                        </div>
                      </div>
                    </div>
                    <Field
                      component={Calendar}
                      name="requiredJoiningDate"
                      label="Required Joining Date"
                      placeholder="Select Date"
                      showIcon
                      dateFormat="dd/mm/yy"
                      validation={validationRule}
                      disabled={!canEditField}
                      minDate={today}
                      required
                    />
                    <Field
                      component={Dropdown}
                      options={urgencyLevelOptions}
                      name="urgencyLevel"
                      label="Urgency Level"
                      placeholder="Select Urgency"
                      validation={validationRule}
                      disabled={!canEditField}
                      required
                    />
                  </div>
                </div>
              </div>
            ),
          },
          ...(jobRequisitionId
            ? [
              {
                header: "Approval Status",
                icon: "pi pi-check-circle",
                content: (
                  <div className="tab-pane-content">
                    <div className="form-section-container">
                      <div className="section-header">
                        <i className="pi pi-flag mr-2"></i>
                        <h3>Status & Actions</h3>
                      </div>
                      <div className="form-grid">
                        {!isInterviewerDraft && (
                          <Field
                            component={Dropdown}
                            options={statusOptions}
                            filter
                            showClear
                            name="status"
                            label="Status"
                            placeholder="Select Status"
                            validation={isAdmin ? "required" : ""}
                            disabled={
                              (isAdmin && isRejected) ||
                              (isAdmin && isApproved) ||
                              ((isHR && isApproved) || (currentLevel === 2 || currentLevel === 1)) ||
                              !(
                                isAdmin ||
                                (isHR &&
                                  (isDraft ||
                                    currentStatus ===
                                    JobRequisitionStatus.PENDING_APPROVAL ||
                                    isApproved)) ||
                                (isInterviewer && (isDraft || isRejected))
                              )
                            }
                            required
                          />
                        )}
                        <Condition when="status" is={JobRequisitionStatus.APPROVED}>
                          <Field
                            component={Dropdown}
                            filter
                            showClear
                            options={jobOpening}
                            name="jobOpeningId"
                            label="Job Opening"
                            placeholder="Select Job Opening"
                            validation={validationRule}
                            disabled={(isAdmin && isApproved) || isInterviewer}
                            required
                          />
                        </Condition>
                        <Condition when="status" is={JobRequisitionStatus.REJECTED}>
                          <Field
                            component={InputText}
                            name="rejectionReason"
                            label="Rejection Reason"
                            placeholder="Enter rejection reason"
                            validation="required"
                            disabled={
                              isInterviewer || (isAdmin && isRejected) || (isHR && isRejected)
                            }
                            required
                          />
                        </Condition>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]
            : []),
        ]}
      />
      <Dialog
        header={
          <div className="select-template-dialog__header">
            <h3 className="select-template-dialog__title">Select Job Template</h3>
            <p className="select-template-dialog__subtitle">
              Choose an active job template to pre-fill requisition details and skills
            </p>
          </div>
        }
        visible={showTemplateDialog}
        style={{ width: "min(720px, 94vw)" }}
        className="select-job-template-dialog"
        onHide={() => setShowTemplateDialog(false)}
        draggable={false}
        resizable={false}
        modal
      >
        <div className="template-dialog-content">
          <div className="template-search-section">
            <div className="search-input-wrapper">
              <i className="pi pi-search search-icon" />
              <InputText
                type="text"
                placeholder="Search templates by name, title, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="template-search-input"
              />
              {searchQuery && (
                <i
                  className="pi pi-times clear-search-icon"
                  onClick={() => setSearchQuery("")}
                  role="button"
                  tabIndex={0}
                />
              )}
            </div>
            <div className="search-meta-bar mt-2">
              <span className="templates-count-badge">
                <i className="pi pi-briefcase" /> {filteredTemplates.length} {filteredTemplates.length === 1 ? "Template" : "Templates"} Available
              </span>
            </div>
          </div>

          <div className="templates-list">
            {filteredTemplates.length === 0 ? (
              <div className="template-empty-state">
                <div className="empty-icon-box">
                  <i className="pi pi-folder-open" />
                </div>
                <h5>No Templates Found</h5>
                <p>No active job templates match your search criteria.</p>
              </div>
            ) : (
              filteredTemplates.map((template) => {
                const isSelected = defaultValue?.jobTemplateId === template.jobTemplateId;
                return (
                  <div
                    key={template.jobTemplateId}
                    className={`template-list-card ${isSelected ? "template-list-card--selected" : ""}`}
                    onClick={() => {
                      setShowTemplateDialog(false);
                      onTemplateChange(template.jobTemplateId);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowTemplateDialog(false);
                        onTemplateChange(template.jobTemplateId);
                      }
                    }}
                  >
                    <div className="template-card-left">
                      <div className="template-avatar">
                        <i className="pi pi-briefcase" />
                      </div>
                      <div className="template-info">
                        <h5 className="template-name">{template.templateName}</h5>
                        <div className="template-pills">
                          {template.jobTitle && (
                            <span className="meta-pill meta-pill--title">
                              <i className="pi pi-id-card" /> {template.jobTitle}
                            </span>
                          )}
                          {template.jobCode && (
                            <span className="meta-pill meta-pill--code">
                              #{template.jobCode}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="template-card-right">
                      {isSelected ? (
                        <span className="selected-pill">
                          <i className="pi pi-check" /> Selected
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="template-select-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTemplateDialog(false);
                            onTemplateChange(template.jobTemplateId);
                          }}
                        >
                          <span>Select</span>
                          <i className="pi pi-arrow-right" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );

};

ManpowerAcquisitionForm.propTypes = {
  departmentOption: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  userOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  skillOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  employmentTypeOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  canEditField: PropTypes.bool,
  validationRule: PropTypes.string,
  statusOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  jobOpening: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })),
  isAdmin: PropTypes.bool,
  isHR: PropTypes.bool,
  isInterviewer: PropTypes.bool,
  isInterviewerDraft: PropTypes.bool,
  jobRequisitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.shape({
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    approvalLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    jobTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  activeTemplates: PropTypes.array,
  fetchTemplateUse: PropTypes.func,
  setDefaultValue: PropTypes.func,
  setFormKey: PropTypes.func,
  formRef: PropTypes.object,
  isEditMode: PropTypes.bool,
};

export default ManpowerAcquisitionForm;
